/**
 * TASK-833 — Investigate opens the whole case.
 *
 * `SignalInvestigation` was the right idea with almost none of the evidence: its DNA body
 * said *"1 position conflict with this client's stated preferences"* and never named the
 * preference, while `context.evidence` sat populated in its own props. This is that panel
 * rebuilt as a canvas tile that **composes the Phase-3 widgets** rather than restating them.
 *
 * ## Why it is a tile, and why it self-fetches
 *
 * DEC-1: the Radar is an alert queue — a deliberately reduced view. The work happens on the
 * canvas, in widgets. A dossier that can only exist inside the Radar's column is a reading
 * surface, not a working one: it cannot sit beside the portfolio it is about, cannot be held
 * open while the advisor acts, and closes the moment they leave the queue.
 *
 * Self-fetching is not a style choice — it is what TASK-836 requires. Only `URL_SAFE_PROPS`
 * reach the address (`canvasUrl.ts`), so a followed link reconstructs this tile from
 * `{clientId, subjectKey, sourceEvent, entityKey, focusIsin, focusSac}` and **nothing else**.
 * A dossier holding its `actionPolicy` or its `alertIds` in props would work when clicked and
 * be empty when linked. So it resolves its own subject out of the Radar payload by
 * `subjectKey`, which is exactly what the in-place frame did — moved, not copied
 * (`signalSubject.findSubject`).
 *
 * ## What it does NOT do
 *
 * It renders no conflict row, no rating and no drift bar of its own. Those are TASK-830, 831
 * and 832, mounted here. A dossier that grew its own would be a fourth implementation that
 * silently stops matching the widgets the moment they change.
 */

import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, ExternalLink, HelpCircle, ShieldAlert } from 'lucide-react';

import { WidgetContainer } from '../WidgetContainer';
import { useRadar } from '../../../hooks/useRadar';
import { useAlertStore } from '../../../stores/alertStore';
import { useMakeTaskFromSignal } from '../../../hooks/useMakeTaskFromSignal';
import type { SignalScope } from '../../../types/signalScope';
import {
  COMPLIANCE_CONDITION_EVENTS,
  TAB_FOR_SOURCE,
  clientEvidenceHref,
  findSubject,
  type SignalSubject,
} from '../../dashboard/signalSubject';
import { SignalUrgency } from '../../dashboard/SignalUrgency';
import { SignalActions } from '../../dashboard/SignalActions';
import { DnaConflict } from './DnaConflict';
import { CIOHouseView } from './CIOHouseView';
import { DriftBars } from './DriftBars';
import { clientDisplayName } from '../../../utils/clientDisplayName';
import { policyPermits } from '../../../utils/signalPolicy';
import { useViewerRole } from '../../../contexts/AccountContext';

const chf = (v?: number | null) =>
  v == null ? null : `CHF ${Math.round(v).toLocaleString('de-CH').replace(/,/g, "'")}`;

/** Explicitly-unavailable, never a positive verdict (TASK-722). */
const Unavailable: React.FC<{ what: string }> = ({ what }) => (
  <p className="flex items-center gap-2 text-xs text-gray-500">
    <HelpCircle className="h-3.5 w-3.5 shrink-0 text-gray-400" aria-hidden />
    {what} is not available for this signal.
  </p>
);

/**
 * The evidence. Branches on the SOURCE EVENT (identity), never on the display label.
 *
 * Each portfolio family mounts the widget that already answers it. The compliance conditions
 * have no widget of their own — their evidence is the condition text the emitter wrote, and
 * inventing a body for them here would be this ticket writing analysis it does not have.
 */
const Evidence: React.FC<{ subject: SignalSubject; scope: SignalScope }> = ({ subject, scope }) => {
  if (COMPLIANCE_CONDITION_EVENTS.has(subject.sourceEvent)) {
    return (
      <div data-testid="dossier-evidence-compliance" className="space-y-1">
        <h4 className="text-sm font-medium text-gray-900">
          {subject.action || 'Compliance condition'}
        </h4>
        {subject.evidence || subject.recommendedAction ? (
          <p className="text-xs text-gray-700">{subject.evidence ?? subject.recommendedAction}</p>
        ) : (
          <Unavailable what="The condition detail" />
        )}
      </div>
    );
  }

  switch (subject.sourceEvent) {
    case 'dna_fit_conflict':
      // TASK-830 names the breached preference and the client's own words behind it — the
      // exact fact this panel used to reduce to a count.
      return (
        <div data-testid="dossier-evidence-dna">
          <DnaConflict {...scope} />
        </div>
      );

    case 'cio_stale_sell':
      return (
        <div data-testid="dossier-evidence-cio">
          <CIOHouseView {...scope} />
        </div>
      );

    case 'portfolio_drift_breach':
      return (
        <div data-testid="dossier-evidence-drift">
          <DriftBars {...scope} />
        </div>
      );

    case 'kyc_staged_pending':
      return (
        <div data-testid="dossier-evidence-kyc" className="space-y-1">
          <h4 className="text-sm font-medium text-gray-900">Profile updates proposed</h4>
          <p className="text-xs text-gray-700">
            {subject.entityLabel || 'Proposals'} awaiting review in the KYC workspace.
          </p>
        </div>
      );

    case 'news_exposure':
      return (
        <div data-testid="dossier-evidence-news" className="space-y-1">
          <h4 className="text-sm font-medium text-gray-900">News exposure</h4>
          {(subject.affectedPositionIds ?? []).length ? (
            <p className="text-xs text-gray-700">
              {subject.affectedPositionIds!.length} held{' '}
              {subject.affectedPositionIds!.length === 1 ? 'position' : 'positions'} matched
              this story.
            </p>
          ) : (
            <Unavailable what="The matched holdings" />
          )}
        </div>
      );

    default:
      return (
        <div data-testid="dossier-evidence-unknown">
          <Unavailable what="A detailed view of this signal type" />
        </div>
      );
  }
};

export function SignalDossier(scope: SignalScope) {
  const { t } = useTranslation('dashboard');
  // TASK-841 / TASK-820: the same `alerts.scope.client` key the Radar and the Alerts page
  // use, so the three surfaces cannot drift into three ways of saying the same thing.
  const unavailableClient = t('alerts.scope.client', 'Client (name unavailable)');
  const navigate = useNavigate();
  // TASK-923: role gates the `assign` affordance below, like every other policy surface.
  const role = useViewerRole();
  const { data, isLoading } = useRadar();
  const resolveAlert = useAlertStore((s) => s.resolve);
  const makeTask = useMakeTaskFromSignal();

  const subjectKey = scope.subjectKey ?? '';
  // Resolved from the CURRENT payload, never from a cached snapshot — a subject the tick has
  // since closed must not be shown as though it were still live.
  const subject = subjectKey
    ? findSubject([...(data?.events ?? []), ...(data?.unresolved ?? [])], subjectKey)
    : null;

  if (!subjectKey) {
    // A dossier with no durable subject has nothing to open ON. It is a summon defect, not a
    // signal that has gone away, and the two must not read the same.
    return (
      <WidgetContainer title="Signal dossier" source="Change Radar">
        <p data-testid="dossier-no-subject" className="text-sm text-gray-600">
          {t('radar.dossier.noSubject',
             'This dossier was opened without a signal subject, so there is nothing to show.')}
        </p>
      </WidgetContainer>
    );
  }

  if (!subject) {
    return (
      <WidgetContainer title="Signal dossier" source="Change Radar">
        {isLoading ? (
          <p data-testid="dossier-loading" className="text-sm text-gray-500">
            {t('radar.loading', 'Scanning your book…')}
          </p>
        ) : (
          // Addressed but absent from the current Radar. Say which of the two it is — closed
          // since, or outside the loaded page — rather than rendering an empty panel that
          // reads as "nothing wrong here" (TASK-722).
          <div
            data-testid="dossier-subject-unavailable"
            className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800"
          >
            <p className="font-medium">
              {t('radar.subjectUnavailable.title', 'This signal is not in the current Radar view.')}
            </p>
            <p className="mt-1 text-xs">
              {t('radar.subjectUnavailable.body',
                 'It may have been resolved since you opened it, or it may fall outside the loaded page. Nothing has been concluded about it.')}
            </p>
          </div>
        )}
      </WidgetContainer>
    );
  }

  const exposure = chf(subject.exposureChf);
  const positions = subject.affectedPositionIds ?? [];
  const portfolioTab = TAB_FOR_SOURCE[subject.sourceEvent] ?? 'overview';

  return (
    <WidgetContainer
      title={`${subject.action}: ${subject.entityLabel || subject.entityKey}`}
      source="Change Radar"
      dataAsOf={subject.detectedAt}
    >
      <section data-testid="signal-dossier" data-subject-key={subject.subjectKey} className="space-y-4">
        {/* ---- What fired, and how urgent ---------------------------------- */}
        <header className="space-y-1">
          <div className="flex items-start gap-2">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-orange-500" aria-hidden />
            <div className="min-w-0">
              <p className="text-xs text-gray-500">
                {/* TASK-841: never fall through to the id. `signalSubject` nulls a name
                    that is not one, so this is the branch the reported row reaches. */}
                {clientDisplayName(subject.clientName, subject.clientId, unavailableClient)}
                {exposure ? ` · ${exposure}` : ''}
                {subject.exposurePct != null ? ` · ${subject.exposurePct.toFixed(1)}%` : ''}
              </p>
            </div>
            {/* TASK-821: the mandatory BAND is one axis; `sla_class` — the dimension
                `get_radar` actually sorts on — is the other. They are rendered together,
                by the one implementation both surfaces share. */}
            {subject.mandatory && (
              <span
                data-testid="dossier-mandatory"
                className="ml-auto inline-flex shrink-0 items-center gap-1 rounded-full bg-red-100 px-2 py-0.5 text-[10px] font-semibold uppercase text-red-800"
              >
                <ShieldAlert className="h-3 w-3" aria-hidden />
                {t('radar.mandatory', 'Mandatory')}
              </span>
            )}
          </div>
          <p className="flex flex-wrap items-center gap-2 text-xs text-gray-500">
            <SignalUrgency
              facts={{
                event_ts: subject.detectedAt,
                due_at: subject.dueAt,
                sla_state: subject.slaState,
                sla_class: subject.slaClass,
                mandatory: subject.mandatory,
              }}
              agePrefix=""
            />
          </p>
          {/* TASK-731: "no exposure" and "exposure not applicable" are different facts. */}
          {!exposure &&
            (subject.exposureNotApplicable ? (
              <p data-testid="dossier-exposure-na" className="text-xs text-gray-500">
                {t('radar.exposureNotApplicable', 'CHF exposure not applicable')}
              </p>
            ) : (
              <Unavailable what="An exposure figure" />
            ))}
        </header>

        {/* ---- The evidence, from the widget that owns it ------------------- */}
        <Evidence subject={subject} scope={scope} />

        {/* ---- The subject: which holdings this is actually about ----------- */}
        <div data-testid="dossier-positions" className="border-t border-gray-100 pt-3">
          <h4 className="text-xs font-medium text-gray-900">
            {t('radar.dossier.positions', 'Affected positions')}
          </h4>
          {positions.length ? (
            <p className="mt-1 text-xs text-gray-700">
              {positions.length} {positions.length === 1 ? 'position' : 'positions'}
              {exposure ? ` · ${exposure}` : ''}
            </p>
          ) : (
            <Unavailable what="The affected positions" />
          )}
        </div>

        {/* ---- What may be done -------------------------------------------- */}
        <div className="border-t border-gray-100 pt-3">
          <SignalActions
            policy={subject.actionPolicy}
            onPolicyAction={async (actionId, rationale) => {
              if (actionId === 'resolve') {
                // TASK-732: the policy-valid acceptance. The server enforces role and
                // rationale; a refusal rejects and the footer shows the reason.
                if (!subject.primaryAlertId) {
                  throw new Error('No alert to resolve for this subject');
                }
                await resolveAlert(subject.primaryAlertId, rationale);
              } else if (actionId === 'escalate') {
                // Escalation raises WORK — it never closes the signal (TASK-729 rule 3).
                // The server re-validates this as the `escalate` disposition (TASK-761);
                // the rationale travels as the description and becomes the charge.
                makeTask({
                  title:
                    `Escalate: ${subject.action}: ${subject.entityLabel || subject.entityKey}` +
                    ` — ${clientDisplayName(subject.clientName, subject.clientId, unavailableClient)}`,
                  source: subject.source || 'radar',
                  description: rationale,
                  dispositionIntent: 'escalate',
                  priority: 'critical',
                  clientId: subject.clientId,
                  subjectKey: subject.subjectKey,
                  subjectSourceEvent: subject.sourceEvent || undefined,
                  originGroupKey: subject.signalGroupKey ?? undefined,
                  primaryAlertId: subject.primaryAlertId ?? undefined,
                  contributingAlertIds: subject.alertIds,
                  relatedEntityType: subject.primaryAlertId ? 'alert' : 'radar_event',
                  relatedEntityId: subject.primaryAlertId ?? subject.originId ?? '',
                });
              }
            }}
          />
        </div>

        {/* ---- Out to the full portfolio ------------------------------------ */}
        <footer className="flex flex-wrap items-center gap-2 border-t border-gray-100 pt-3">
          <button
            type="button"
            data-testid="dossier-open-portfolio"
            onClick={() =>
              // `impacted_clients` carries no `portfolio_id` (TASK-726's documented gap), so
              // the deep link targets Client Level. A tab that view does not have falls back
              // to its default rather than writing a param the route silently discards.
              navigate(clientEvidenceHref(subject.clientId, portfolioTab))
            }
            className="inline-flex items-center gap-1 rounded px-2 py-1 text-xs text-blue-700 hover:bg-blue-50"
          >
            <ExternalLink className="h-3 w-3" aria-hidden /> Open full portfolio
          </button>
          {/* TASK-732: task creation is the `assign` disposition — offered only when the
              server policy lists it (no policy → no restriction, as before). */}
          {policyPermits(subject.actionPolicy, 'assign', role) && (
            <button
              type="button"
              data-testid="dossier-create-task"
              onClick={() =>
                makeTask({
                  title:
                    `${subject.action}: ${subject.entityLabel || subject.entityKey}` +
                    ` — ${clientDisplayName(subject.clientName, subject.clientId, unavailableClient)}`,
                  source: subject.source || 'radar',
                  clientId: subject.clientId,
                  subjectKey: subject.subjectKey,
                  subjectSourceEvent: subject.sourceEvent || undefined,
                  originGroupKey: subject.signalGroupKey ?? undefined,
                  primaryAlertId: subject.primaryAlertId ?? undefined,
                  contributingAlertIds: subject.alertIds,
                  relatedEntityType: subject.primaryAlertId ? 'alert' : 'radar_event',
                  relatedEntityId: subject.primaryAlertId ?? subject.originId ?? '',
                })
              }
              className="rounded bg-indigo-600 px-2 py-1 text-xs font-medium text-white hover:bg-indigo-700"
            >
              {t('radar.dossier.createTask', 'Create task')}
            </button>
          )}
        </footer>
      </section>
    </WidgetContainer>
  );
}

export default SignalDossier;
